import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue
} from "@veritrellis/ui";
import { X } from "lucide-react";
import { ConditionValueEditor } from "./ConditionValueEditor.js";
import { fieldCatalog, fieldGroups, getFieldDef, opLabelInline } from "./fieldCatalog.js";
import type { BuilderCondition } from "./builderTypes.js";

type Props = {
  condition: BuilderCondition;
  onChange: (updated: BuilderCondition) => void;
  onRemove: () => void;
  disabled?: boolean;
};

const catalogKeys = new Set(fieldCatalog.map((f) => f.key));

export const ConditionRow = ({ condition, onChange, onRemove, disabled = false }: Props) => {
  const field = getFieldDef(condition.field);

  const handleFieldChange = (key: string) => {
    const newField = getFieldDef(key as BuilderCondition["field"]);
    const defaultOp = newField.operators[0] ?? "eq";
    const defaultValue: BuilderCondition["value"] =
      newField.type === "number"
        ? 0
        : newField.type === "enum"
          ? (newField.enumValues?.[0] ?? "")
          : "";
    onChange({ ...condition, field: newField.key, op: defaultOp, value: defaultValue });
  };

  const handleOpChange = (op: string) => {
    const isMulti = op === "in" || op === "not_in";
    const currentIsArray = Array.isArray(condition.value);
    const value: BuilderCondition["value"] = isMulti
      ? currentIsArray
        ? condition.value
        : field.type === "enum"
          ? field.enumValues?.[0] != null
            ? [field.enumValues[0]]
            : []
          : [String(condition.value)]
      : currentIsArray
        ? ((condition.value as string[])[0] ?? (field.type === "number" ? 0 : ""))
        : condition.value;
    onChange({ ...condition, op: op as BuilderCondition["op"], value });
  };

  return (
    <div className="admin-condition-row">
      <div className="admin-condition-segments">
        <div className="admin-condition-segment admin-condition-segment-field">
          <Select value={condition.field} onValueChange={handleFieldChange} disabled={disabled}>
            <SelectTrigger
              className="admin-condition-trigger admin-condition-trigger-field"
              title={field.description}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {fieldGroups.map((group) => (
                <SelectGroup key={group.label}>
                  <SelectLabel>{group.label}</SelectLabel>
                  {group.fields
                    .filter((k) => catalogKeys.has(k))
                    .map((key) => {
                      const def = getFieldDef(key);
                      return (
                        <SelectItem key={key} value={key}>
                          {def.label}
                        </SelectItem>
                      );
                    })}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="admin-condition-segment admin-condition-segment-op">
          <Select value={condition.op} onValueChange={handleOpChange} disabled={disabled}>
            <SelectTrigger className="admin-condition-trigger admin-condition-trigger-op">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {field.operators.map((o) => (
                <SelectItem key={o} value={o}>
                  {opLabelInline(o, field.type)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <ConditionValueEditor
          field={field}
          condition={condition}
          onChange={onChange}
          disabled={disabled}
        />
      </div>

      <button
        type="button"
        className="admin-condition-remove"
        onClick={onRemove}
        disabled={disabled}
        aria-label="Remove condition"
      >
        <X style={{ width: 14, height: 14 }} />
      </button>
    </div>
  );
};
